import type { HotelActivity, HotelRoom } from '../../_types';

type OrderedEntity = {
  id: string | number;
};

export const withHotelPriority = <T extends object>(items: T[]) =>
  items.map((item, index) => ({
    ...item,
    hotelPriority: index + 1,
  }));

export const withRoomPriority = (rooms: HotelRoom[]) =>
  rooms.map((room, index) => ({
    ...room,
    hotelPriority: index + 1,
    priority: index + 1,
  }));

export const withAlwaysIncludedActivityPriority = (
  activities: HotelActivity[]
) => {
  return activities.reduce<
    (HotelActivity & { hotelPriority: number | undefined })[]
  >((acc, activity) => {
    const priority = activity.isAlwaysIncludedWithNight
      ? acc.filter((a) => a.isAlwaysIncludedWithNight).length + 1
      : undefined;

    return [...acc, { ...activity, hotelPriority: priority }];
  }, []);
};

/**
 * Merges a reordered subset back into the full list,
 * preserving positions of items that were not part of the reorder.
 *
 * Example: full list [A, B, C, D], reordered section [C, A] (originally at positions 0 and 2)
 * → result [C, B, A, D]
 */
export const applyReorderedSection = <T extends OrderedEntity>(
  items: T[],
  reorderedSectionItems: T[]
): T[] => {
  const reorderedIds = new Set(reorderedSectionItems.map((item) => item.id));
  let reorderedIndex = 0;

  return items.map((item) => {
    if (!reorderedIds.has(item.id)) {
      return item;
    }

    const reorderedItem = reorderedSectionItems[reorderedIndex];

    reorderedIndex += 1;

    return reorderedItem;
  });
};
